import { useCallback, useState } from 'react';
import { exportCriticalResultsDashboard } from '../services/criticalResultsDashboardService';
import type {
  CriticalResultsDashboardFilters,
  CriticalResultsExportDataset,
  CriticalResultsExportFormat,
} from '../types/criticalResultsDashboard.types';

interface UiNotificationService {
  show: (options: {
    title: string;
    message: string;
    type: 'error' | 'success' | 'info' | 'warning';
    duration?: number;
  }) => void;
}

export interface CriticalResultsExportOptions {
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC' | 'asc' | 'desc';
  search?: string;
}

const FORMAT_LABELS: Record<CriticalResultsExportFormat, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
  excel: 'Excel',
  pdf: 'PDF',
};

export function useCriticalResultsExport(uiNotificationService?: UiNotificationService | null) {
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const handleExport = useCallback(
    async (
      format: CriticalResultsExportFormat,
      dataset: CriticalResultsExportDataset,
      filters: CriticalResultsDashboardFilters,
      options: CriticalResultsExportOptions = {}
    ) => {
      const formatLabel = FORMAT_LABELS[format] || format.toUpperCase();
      setExportMenuOpen(false);
      setExporting(true);

      uiNotificationService?.show({
        title: 'Export in progress',
        message: `Generating ${formatLabel} export with current filters…`,
        type: 'info',
        duration: 3000,
      });

      try {
        await exportCriticalResultsDashboard(dataset, format, {
          ...filters,
          sortBy: options.sortBy,
          sortOrder: options.sortOrder,
          search: options.search?.trim() || undefined,
        });

        uiNotificationService?.show({
          title: 'Export complete',
          message: `Your ${formatLabel} file download should begin shortly.`,
          type: 'success',
          duration: 4000,
        });
      } catch (error) {
        uiNotificationService?.show({
          title: 'Export failed',
          message: error instanceof Error ? error.message : 'Unable to export dashboard data',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setExporting(false);
      }
    },
    [uiNotificationService]
  );

  const toggleExportMenu = useCallback(() => {
    if (exporting) {
      return;
    }
    setExportMenuOpen(open => !open);
  }, [exporting]);

  return {
    exporting,
    exportMenuOpen,
    setExportMenuOpen,
    toggleExportMenu,
    handleExport,
  };
}
